export const PLAYER_STATES = Object.freeze({
  PREPARING: 'preparing',
  CONNECTING: 'connecting',
  STABILIZING: 'stabilizing',
  PLAYING: 'playing',
  PAUSED: 'paused',
  BUFFERING: 'buffering',
  // Player D freeze profile: the picture is intentionally held on the last
  // frame during an upstream outage. No blocking overlay, only a discreet chip.
  FROZEN: 'frozen',
  // Player D screen manager: the account has no free screen; media stays
  // detached while the manager UI lists the active screens.
  WAITING_SLOT: 'waitingSlot',
  OFFLINE: 'offline',
  WAITING_RETRY: 'waitingRetry',
  RECOVERED: 'recovered',
  TERMINAL_ERROR: 'terminalError'
});

const RETRYABLE_HTTP_STATUSES = new Set([408, 423, 425, 429]);
const TERMINAL_HTTP_STATUSES = new Set([401, 403, 404]);

export function isRetryableHttpStatus(statusCode) {
  const status = Number(statusCode);
  return RETRYABLE_HTTP_STATUSES.has(status) || (status >= 500 && status <= 599);
}

export function extractHttpStatus(value, depth = 0, seen = new WeakSet()) {
  if (depth > 5 || value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return depth === 0 && value >= 100 && value <= 599 ? value : null;
  }
  if (typeof value !== 'object') return null;
  if (seen.has(value)) return null;
  seen.add(value);

  for (const key of ['status', 'statusCode', 'code']) {
    const candidate = Number(value[key]);
    if (candidate >= 100 && candidate <= 599) return candidate;
  }
  for (const nested of Object.values(value)) {
    const status = extractHttpStatus(nested, depth + 1, seen);
    if (status !== null) return status;
  }
  return null;
}

/**
 * Turn media-library failures into a small, user-safe contract. Unknown
 * transport/network failures remain recoverable; only explicit permanent
 * HTTP responses and an unsupported HLS runtime stop automatic attempts.
 */
export function classifyPlaybackFailure(value, fallbackReason = 'network') {
  if (fallbackReason === 'hlsUnsupported') {
    return {retryable: false, reason: 'hlsUnsupported', statusCode: null};
  }

  const statusCode = extractHttpStatus(value);
  if (TERMINAL_HTTP_STATUSES.has(statusCode)) {
    const reasons = {401: 'sessionExpired', 403: 'accessDenied', 404: 'channelUnavailable'};
    return {retryable: false, reason: reasons[statusCode], statusCode};
  }
  if (statusCode !== null && !isRetryableHttpStatus(statusCode)) {
    // Media servers use several non-standard successful/redirect responses.
    // Do not make an unknown status terminal unless the contract names it.
    return {retryable: true, reason: fallbackReason, statusCode};
  }
  return {retryable: true, reason: fallbackReason, statusCode};
}

export function retryCountdownSeconds(retryAt, now = Date.now()) {
  if (!Number.isFinite(retryAt)) return 0;
  return Math.max(0, Math.ceil((retryAt - now) / 1000));
}

const REASON_MESSAGES = {
  autoplayBlocked: 'El navegador necesita que pulses reproducir para iniciar el vídeo.',
  accessDenied: 'Esta cuenta no tiene permiso para abrir la transmisión.',
  channelUnavailable: 'Este canal ya no está disponible.',
  emptyStartupBuffer: 'La señal no ha entregado vídeo utilizable.',
  hlsUnsupported: 'Este navegador no permite reproducir transmisiones HLS.',
  loadingComplete: 'La señal de origen ha terminado inesperadamente.',
  network: 'No se ha podido mantener la conexión con la transmisión.',
  offline: 'Comprueba tu conexión. Volveremos a intentarlo cuando haya red.',
  playbackStall: 'La imagen ha dejado de avanzar.',
  sessionExpired: 'La sesión ya no permite abrir esta transmisión.',
  slotAvailable: 'Hay una conexión disponible de nuevo.',
  screenLimit: 'No hay pantallas libres en esta cuenta.',
  sessionStopped: 'La sesión de emisión se ha renovado.',
  startupTimeout: 'La transmisión está tardando demasiado en responder.',
  stall: 'La imagen se ha quedado detenida.',
  statusLost: 'Se ha perdido el contacto con el servidor de emisión.',
  streamEnded: 'La transmisión se ha cerrado inesperadamente.',
  transport: 'El formato de la transmisión ha cambiado.',
  upstreamOutage: 'La señal de origen se ha interrumpido.'
};

export function playbackReasonMessage(reason) {
  return REASON_MESSAGES[reason] || 'La señal se ha interrumpido temporalmente.';
}

export function playbackStatusMessage(status, options = {}) {
  const {
    attempt = 0,
    retryAt = null,
    now = Date.now(),
    reason = null,
    bufferSeconds = null
  } = options;
  const seconds = retryCountdownSeconds(retryAt, now);
  switch (status) {
    case PLAYER_STATES.PREPARING:
      return {title: 'Preparando reproductor…', detail: 'Configurando la mejor conexión.'};
    case PLAYER_STATES.CONNECTING:
      return {title: 'Conectando con la transmisión…', detail: 'Esto puede tardar unos segundos.'};
    case PLAYER_STATES.STABILIZING:
      return {
        title: 'Estabilizando emisión…',
        detail:
          bufferSeconds === null
            ? 'Creando una reserva de vídeo para evitar cortes.'
            : `Reserva disponible: ${Math.max(0, bufferSeconds).toFixed(1)} s.`
      };
    case PLAYER_STATES.PAUSED:
      return {
        title: reason === 'autoplayBlocked' ? 'Pulsa para iniciar' : 'Emisión pausada',
        detail:
          reason === 'autoplayBlocked'
            ? playbackReasonMessage(reason)
            : 'Pulsa reproducir cuando quieras continuar.'
      };
    case PLAYER_STATES.BUFFERING:
      return {title: 'Recuperando la señal…', detail: playbackReasonMessage(reason)};
    case PLAYER_STATES.FROZEN:
      return {
        title: 'Reconectando…',
        detail: 'La imagen se mantiene congelada mientras se restablece la señal.'
      };
    case PLAYER_STATES.WAITING_SLOT:
      return {
        title: 'Sin pantallas libres',
        detail: 'Cierra una pantalla para empezar a ver este canal.'
      };
    case PLAYER_STATES.OFFLINE:
      return {title: 'Sin conexión a Internet', detail: playbackReasonMessage('offline')};
    case PLAYER_STATES.WAITING_RETRY:
      return {
        title: seconds > 0 ? `Reconectando en ${seconds} s…` : 'Reconectando ahora…',
        detail: `${playbackReasonMessage(reason)}${attempt > 0 ? ` Intento ${attempt}.` : ''}`
      };
    case PLAYER_STATES.RECOVERED:
      return {title: 'Señal recuperada', detail: 'La reproducción vuelve a ser estable.'};
    case PLAYER_STATES.TERMINAL_ERROR:
      return {title: 'No se puede reproducir este canal', detail: playbackReasonMessage(reason)};
    case PLAYER_STATES.PLAYING:
    default:
      return {title: 'En directo', detail: 'La transmisión está activa.'};
  }
}
